// CMS Dashboard Page
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useCMS } from './CMSContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, CreditCard, Ticket, Calendar, TrendingUp, TrendingDown, 
  DollarSign, ShoppingCart, RefreshCw, AlertCircle, Mail, Send, Bell
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { toast } from 'sonner';

import { CMSSystemHealthWidget } from './CMSSystemHealth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper hook for CMS theme classes
const useCMSTheme = () => {
  const { cmsTheme } = useCMS();
  return {
    theme: cmsTheme,
    card: cmsTheme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200 shadow-sm',
    cardInner: cmsTheme === 'dark' ? 'bg-slate-900' : 'bg-gray-50',
    text: cmsTheme === 'dark' ? 'text-white' : 'text-gray-900',
    textMuted: cmsTheme === 'dark' ? 'text-slate-400' : 'text-gray-500',
    textMuted2: cmsTheme === 'dark' ? 'text-slate-500' : 'text-gray-400',
    input: cmsTheme === 'dark' ? 'bg-slate-900 border-slate-600' : 'bg-white border-gray-300',
    chartGrid: cmsTheme === 'dark' ? '#334155' : '#e5e7eb',
    chartAxis: cmsTheme === 'dark' ? '#94a3b8' : '#6b7280',
    tooltip: cmsTheme === 'dark' ? { bg: '#1e293b', border: '#334155', label: '#f8fafc' } : { bg: '#ffffff', border: '#e5e7eb', label: '#111827' },
  };
};

const CMSDashboard = () => {
  const { getAuthHeaders, hasRole, cmsTheme } = useCMS();
  const t = useCMSTheme();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartDays, setChartDays] = useState('30');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [targets, setTargets] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, chartRes, summaryRes, targetsRes] = await Promise.all([
        axios.get(`${API}/cms/dashboard/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cms/dashboard/charts?days=${chartDays}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cms/reports/daily-summary`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/cms/dashboard/targets`, { headers: getAuthHeaders() }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setChartData(chartRes.data);
      setDailySummary(summaryRes.data);
      setTargets(targetsRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendDailyReport = async () => {
    setSendingReport(true);
    try {
      await axios.post(`${API}/cms/reports/send-daily-summary`, {}, { headers: getAuthHeaders() });
      toast.success('Daily summary sent to administration@freestays.eu');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [showStripeStatus, setShowStripeStatus] = useState(false);

  const fetchStripeStatus = async () => {
    try {
      const res = await axios.get(`${API}/cms/sync/stripe/status`, { headers: getAuthHeaders() });
      setStripeStatus(res.data);
      return res.data;
    } catch (err) {
      console.error('Stripe status fetch failed:', err);
      return null;
    }
  };

  const handleStripeSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/cms/sync/stripe`, {}, { headers: getAuthHeaders() });
      const d = res.data || {};
      const synced = d.synced ?? 0;
      const updated = d.updated ?? 0;
      const enriched = d.enriched ?? 0;
      const errs = d.total_errors ?? (d.errors?.length || 0);
      toast.success(
        `Stripe sync complete: ${synced} new, ${updated} updated, ${enriched} enriched`
        + (errs ? ` — ${errs} error${errs === 1 ? '' : 's'}` : '')
      );
      setLastSync(new Date());
      fetchStripeStatus();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stripe sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // Auto-sync Stripe on initial load + fetch status
  useEffect(() => {
    const autoSync = async () => {
      try {
        await axios.post(`${API}/cms/sync/stripe`, {}, { headers: getAuthHeaders() });
        setLastSync(new Date());
      } catch (err) {
        // Silently fail auto-sync
        console.log('Auto-sync skipped:', err.message);
      }
    };
    autoSync();
    fetchStripeStatus();
  }, []);

  useEffect(() => {
    fetchData();
  }, [chartDays]);

  const isManager = stats?.is_manager_view;

  const statCards = stats ? (isManager ? [
    { title: 'B2B Orders', value: stats.total_bookings, icon: ShoppingCart, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { title: 'Passes Sold', value: stats.total_passes, icon: Ticket, color: 'text-green-400', bg: 'bg-green-500/10' },
    { title: 'Acquired Users', value: stats.total_users, icon: Users, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    { title: 'B2B Revenue', value: `€${stats.total_revenue.toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { title: 'Commission Pending', value: `€${(stats.commission_pending || 0).toLocaleString()}`, icon: DollarSign, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
    { title: 'Commission Paid', value: `€${(stats.commission_paid || 0).toLocaleString()}`, icon: CreditCard, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  ] : [
    { title: 'Total Users', value: stats.total_users, icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { title: 'Active Passes', value: stats.active_passes, icon: Ticket, color: 'text-green-400', bg: 'bg-green-500/10' },
    { title: 'Monthly Revenue', value: `€${stats.monthly_revenue.toLocaleString()}`, icon: DollarSign, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
    { title: 'Monthly Bookings', value: stats.monthly_bookings, icon: ShoppingCart, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    { title: 'Total Revenue', value: `€${stats.total_revenue.toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { title: 'Pending Refunds', value: stats.pending_refunds, icon: AlertCircle, color: stats.pending_refunds > 0 ? 'text-red-400' : 'text-slate-400', bg: stats.pending_refunds > 0 ? 'bg-red-500/10' : 'bg-slate-500/10' },
  ]) : [];

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${t.text}`}>Dashboard</h1>
          <p className={t.textMuted}>{isManager ? 'Your sales & commission overview' : 'Overview of your Freestays platform'}</p>
        </div>
        <div className="flex items-center gap-2">
          {!isManager && (
            <Button 
              variant="outline" 
              onClick={handleStripeSync} 
              disabled={syncing} 
              className={t.input}
              data-testid="stripe-sync-btn"
            >
              <CreditCard className={`w-4 h-4 mr-2 ${syncing ? 'animate-pulse' : ''}`} />
              {syncing ? 'Syncing all Stripe data...' : 'Sync Stripe'}
            </Button>
          )}
          <Button variant="outline" onClick={fetchData} disabled={loading} className={t.input}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {lastSync && (
        <p className={`text-xs ${t.textMuted2}`}>
          Last Stripe sync: {lastSync.toLocaleTimeString()}
          {hasRole && hasRole('super_admin') && (
            <>
              {' · '}
              <button
                type="button"
                className="underline hover:text-foreground"
                onClick={() => { fetchStripeStatus(); setShowStripeStatus(s => !s); }}
                data-testid="toggle-stripe-status"
              >
                {showStripeStatus ? 'Hide debug' : 'Show debug'}
              </button>
            </>
          )}
        </p>
      )}

      {showStripeStatus && stripeStatus && hasRole && hasRole('super_admin') && (
        <div className="text-xs p-3 rounded-lg border bg-secondary/30 space-y-1.5 font-mono" data-testid="stripe-debug-panel">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={stripeStatus.stripe_mode === 'live' ? 'bg-red-100 text-red-700 border-red-300' : 'bg-blue-100 text-blue-700 border-blue-300'}>
              MODE: {String(stripeStatus.stripe_mode).toUpperCase()}
            </Badge>
            {stripeStatus.is_configured ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Key configured ✓</Badge>
            ) : (
              <Badge className="bg-red-100 text-red-700 border-red-300">Key missing ❌</Badge>
            )}
            <span>Active key: <b>{stripeStatus.active_key_masked || '—'}</b></span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>Live key in DB: {stripeStatus.live_key_present ? `✓ ${stripeStatus.live_key_masked}` : '✗'}</div>
            <div>Test key in DB: {stripeStatus.test_key_present ? `✓ ${stripeStatus.test_key_masked}` : '✗'}</div>
            <div>Payments in DB (total): {stripeStatus.payments_in_db_total}</div>
            <div>Payments last 30d: {stripeStatus.payments_in_db_last_30d}</div>
          </div>
          {stripeStatus.last_sync && (
            <div className="text-muted-foreground">
              Last sync by: {stripeStatus.synced_by || '—'} · {String(stripeStatus.last_sync)}
            </div>
          )}
          {stripeStatus.details && Object.keys(stripeStatus.details).length > 0 && (
            <div className="text-muted-foreground">
              Last result: {Object.entries(stripeStatus.details).map(([k, v]) => `${k}=${v}`).join(' · ')}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat, i) => (
          <Card key={i} className={t.card}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${t.textMuted}`}>{stat.title}</p>
                  <p className={`text-2xl font-bold mt-1 ${t.text}`}>{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${stat.bg}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* System Health Widget — super admin only */}
      {hasRole && hasRole('super_admin') && <CMSSystemHealthWidget />}

      {/* Manager Target Tracking */}
      {isManager && targets?.current && targets.current.monthly_revenue_target > 0 && (
        <Card className={t.card} data-testid="manager-targets">
          <CardHeader>
            <CardTitle className={`text-lg ${t.text} flex items-center gap-2`}>
              <TrendingUp className="w-5 h-5 text-purple-400" />
              Maandelijks Target — {targets.current.month_label}
            </CardTitle>
            <CardDescription className={t.textMuted}>
              Dag {targets.current.days_passed} van {targets.current.days_in_month} — {targets.current.days_remaining} dagen resterend
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Revenue Progress */}
            <div>
              <div className="flex justify-between text-sm mb-1.5">
                <span className={t.textMuted}>Omzet</span>
                <span className={`font-semibold ${t.text}`}>
                  €{targets.current.current_revenue.toLocaleString()} / €{targets.current.monthly_revenue_target.toLocaleString()}
                </span>
              </div>
              <div className={`w-full h-3 rounded-full ${t.cardInner}`}>
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    targets.current.revenue_pct >= 100 ? 'bg-green-500' :
                    targets.current.revenue_pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(100, targets.current.revenue_pct)}%` }}
                />
              </div>
              <p className={`text-xs mt-1 ${t.textMuted2}`}>{targets.current.revenue_pct}% behaald</p>
            </div>

            {/* Orders Progress */}
            {targets.current.monthly_orders_target > 0 && (
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className={t.textMuted}>Orders</span>
                  <span className={`font-semibold ${t.text}`}>
                    {targets.current.current_orders} / {targets.current.monthly_orders_target}
                  </span>
                </div>
                <div className={`w-full h-3 rounded-full ${t.cardInner}`}>
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${
                      targets.current.orders_pct >= 100 ? 'bg-green-500' :
                      targets.current.orders_pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(100, targets.current.orders_pct)}%` }}
                  />
                </div>
                <p className={`text-xs mt-1 ${t.textMuted2}`}>{targets.current.orders_pct}% behaald</p>
              </div>
            )}

            {/* Summary row */}
            <div className={`grid grid-cols-2 gap-4 pt-3 border-t ${t.theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
              <div className={`p-3 rounded-lg ${t.cardInner}`}>
                <p className={`text-xs ${t.textMuted}`}>Commissie verdiend</p>
                <p className={`text-lg font-bold ${t.text}`}>€{targets.current.commission_earned?.toLocaleString()}</p>
              </div>
              <div className={`p-3 rounded-lg ${t.cardInner}`}>
                <p className={`text-xs ${t.textMuted}`}>Dagelijks nodig</p>
                <p className={`text-lg font-bold ${t.text}`}>€{targets.current.daily_revenue_needed?.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manager Target History */}
      {isManager && targets?.history?.length > 0 && targets.current?.monthly_revenue_target > 0 && (
        <Card className={t.card} data-testid="manager-target-history">
          <CardHeader>
            <CardTitle className={`text-lg ${t.text}`}>Target Historie</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {targets.history.map((h) => (
                <div key={h.month} className={`flex items-center justify-between p-3 rounded-lg ${t.cardInner}`}>
                  <span className={`text-sm font-medium ${t.text}`}>{h.month_label}</span>
                  <div className="flex items-center gap-4">
                    <span className={`text-sm ${t.textMuted}`}>€{h.revenue.toLocaleString()}</span>
                    <div className="w-24">
                      <div className={`w-full h-2 rounded-full ${t.theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`}>
                        <div
                          className={`h-full rounded-full ${h.target_met ? 'bg-green-500' : 'bg-red-400'}`}
                          style={{ width: `${Math.min(100, h.revenue_pct)}%` }}
                        />
                      </div>
                    </div>
                    <Badge variant={h.target_met ? 'default' : 'secondary'} className={h.target_met ? 'bg-green-600 text-white' : ''}>
                      {h.revenue_pct}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin: Manager Performance Overview */}
      {!isManager && targets?.managers?.length > 0 && (
        <Card className={t.card} data-testid="admin-manager-targets">
          <CardHeader>
            <CardTitle className={`text-lg ${t.text} flex items-center gap-2`}>
              <Users className="w-5 h-5 text-blue-400" />
              Manager Performance — {targets.month}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {targets.managers.map((mgr) => (
                <div key={mgr.email} className={`p-4 rounded-lg ${t.cardInner}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className={`font-medium ${t.text}`}>{mgr.name}</span>
                      <p className={`text-xs ${t.textMuted}`}>{mgr.email}</p>
                    </div>
                    <Badge variant={mgr.revenue_pct >= 100 ? 'default' : mgr.revenue_pct >= 50 ? 'secondary' : 'destructive'}
                      className={mgr.revenue_pct >= 100 ? 'bg-green-600' : ''}>
                      {mgr.revenue_pct}%
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className={`w-full h-2.5 rounded-full ${t.theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'}`}>
                        <div
                          className={`h-full rounded-full transition-all ${
                            mgr.revenue_pct >= 100 ? 'bg-green-500' :
                            mgr.revenue_pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(100, mgr.revenue_pct)}%` }}
                        />
                      </div>
                    </div>
                    <span className={`text-sm whitespace-nowrap ${t.textMuted}`}>
                      €{mgr.current_revenue.toLocaleString()} / €{mgr.monthly_revenue_target.toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Registrations Chart */}
        <Card className={t.card}>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className={`text-lg ${t.text}`}>User Registrations</CardTitle>
              <CardDescription className={t.textMuted}>New users over time</CardDescription>
            </div>
            <Select value={chartDays} onValueChange={setChartDays}>
              <SelectTrigger className={`w-32 ${t.input}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {chartData?.registrations && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData.registrations}>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.chartGrid} />
                    <XAxis dataKey="date" stroke={t.chartAxis} tick={{ fontSize: 12 }} />
                    <YAxis stroke={t.chartAxis} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: t.tooltip.bg, border: `1px solid ${t.tooltip.border}` }}
                      labelStyle={{ color: t.tooltip.label }}
                    />
                    <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Revenue Chart */}
        <Card className={t.card}>
          <CardHeader>
            <CardTitle className={`text-lg ${t.text}`}>Revenue</CardTitle>
            <CardDescription className={t.textMuted}>Daily revenue over time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {chartData?.revenue && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.revenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.chartGrid} />
                    <XAxis dataKey="date" stroke={t.chartAxis} tick={{ fontSize: 12 }} />
                    <YAxis stroke={t.chartAxis} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: t.tooltip.bg, border: `1px solid ${t.tooltip.border}` }}
                      labelStyle={{ color: t.tooltip.label }}
                      formatter={(value) => [`€${value}`, 'Revenue']}
                    />
                    <Bar dataKey="amount" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Daily Summary Card */}
        <Card className={`${t.card} lg:col-span-2`} data-testid="daily-summary-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className={`text-lg flex items-center gap-2 ${t.text}`}>
                <Mail className="h-5 w-5 text-blue-400" />
                Daily Summary Report
              </CardTitle>
              <CardDescription className={t.textMuted}>Yesterday's performance ({dailySummary?.date || 'Loading...'})</CardDescription>
            </div>
            {hasRole('super_admin') && (
              <Button 
                size="sm" 
                onClick={handleSendDailyReport} 
                disabled={sendingReport}
                data-testid="send-daily-report-btn"
              >
                {sendingReport ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send Now
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {dailySummary ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`${t.cardInner} p-3 rounded-lg`}>
                  <p className={`text-xs ${t.textMuted}`}>New Users</p>
                  <p className={`text-xl font-bold ${t.text}`}>{dailySummary.new_users}</p>
                  <p className={`text-xs ${dailySummary.user_growth_wow >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {dailySummary.user_growth_wow >= 0 ? '+' : ''}{dailySummary.user_growth_wow}% WoW
                  </p>
                </div>
                <div className={`${t.cardInner} p-3 rounded-lg`}>
                  <p className={`text-xs ${t.textMuted}`}>Revenue</p>
                  <p className="text-xl font-bold text-green-400">€{dailySummary.revenue.toFixed(2)}</p>
                  <p className={`text-xs ${t.textMuted2}`}>{dailySummary.transactions} transactions</p>
                </div>
                <div className={`${t.cardInner} p-3 rounded-lg`}>
                  <p className={`text-xs ${t.textMuted}`}>Bookings</p>
                  <p className={`text-xl font-bold ${t.text}`}>{dailySummary.new_bookings}</p>
                  <p className="text-xs text-green-400">{dailySummary.confirmed_bookings} confirmed</p>
                </div>
                <div className={`${t.cardInner} p-3 rounded-lg`}>
                  <p className={`text-xs ${t.textMuted}`}>Alerts</p>
                  <div className="flex items-center gap-2">
                    <p className={`text-xl font-bold ${dailySummary.unresolved_alerts > 0 ? 'text-red-400' : t.text}`}>
                      {dailySummary.unresolved_alerts}
                    </p>
                    {dailySummary.unresolved_alerts > 0 && (
                      <Badge className="bg-red-500 text-white text-xs">Action Required</Badge>
                    )}
                  </div>
                  <p className={`text-xs ${t.textMuted2}`}>{dailySummary.fraud_alerts} fraud alerts</p>
                </div>
                <div className={`${t.cardInner} p-3 rounded-lg col-span-2`}>
                  <p className={`text-xs ${t.textMuted}`}>Active Passes</p>
                  <div className="flex items-center justify-between">
                    <p className={`text-xl font-bold ${t.text}`}>{dailySummary.active_passes}</p>
                    {dailySummary.expiring_passes_7d > 0 && (
                      <Badge className="bg-yellow-500 text-white">{dailySummary.expiring_passes_7d} expiring in 7 days</Badge>
                    )}
                  </div>
                  <p className={`text-xs ${t.textMuted2}`}>{dailySummary.expiring_passes_30d} expiring in 30 days</p>
                </div>
                <div className={`${t.cardInner} p-3 rounded-lg col-span-2`}>
                  <p className={`text-xs ${t.textMuted}`}>Total Platform Users</p>
                  <p className={`text-xl font-bold ${t.text}`}>{dailySummary.total_users.toLocaleString()}</p>
                  <p className={`text-xs ${t.textMuted2}`}>{dailySummary.new_users_with_pass} new with pass</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className={`w-6 h-6 animate-spin ${t.textMuted2}`} />
              </div>
            )}
            <p className={`text-xs ${t.textMuted2} mt-4 text-center`}>
              📧 Automatic daily report sent at 7:00 AM UTC to administration@freestays.eu
            </p>
          </CardContent>
        </Card>

        {/* Bookings Chart */}
        <Card className={`${t.card} lg:col-span-2`}>
          <CardHeader>
            <CardTitle className={`text-lg ${t.text}`}>Bookings</CardTitle>
            <CardDescription className={t.textMuted}>Daily booking count</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {chartData?.bookings && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData.bookings}>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.chartGrid} />
                    <XAxis dataKey="date" stroke={t.chartAxis} tick={{ fontSize: 12 }} />
                    <YAxis stroke={t.chartAxis} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: t.tooltip.bg, border: `1px solid ${t.tooltip.border}` }}
                      labelStyle={{ color: t.tooltip.label }}
                    />
                    <Line type="monotone" dataKey="count" stroke="#a855f7" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CMSDashboard;
